import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import {
  useProductRecommendations,
  type ProductRecommendationStatus,
} from '@/shared/hooks/api/product'
import {
  parseRecordProductContextOutput,
  type ProductRecommendationField,
} from './llmChatToolOutput'

export type LlmChatToolProductRecommendationsCardProps = {
  output: unknown
  sessionId: string | null | undefined
  productId: string | null | undefined
  isChatStreamBusy?: boolean
  onContinueAfterCatalogSave?: (recommendationId: string) => void
  onContinueAfterCatalogDismiss?: (recommendationId: string) => void
}

function formatFieldValue(value: string | number | null, fallbackLabel: string): string {
  if (value === null || value === undefined) {
    return fallbackLabel
  }
  if (typeof value === 'string' && value.trim().length === 0) {
    return fallbackLabel
  }
  return String(value)
}

function recommendationStatusLabel(
  status: ProductRecommendationStatus,
  translate: (key: string) => string
): string {
  if (status === 'accepted') {
    return translate('recommendations.status.accepted')
  }
  if (status === 'dismissed') {
    return translate('recommendations.status.dismissed')
  }
  if (status === 'superseded') {
    return translate('recommendations.status.superseded')
  }
  if (status === 'stale') {
    return translate('recommendations.status.stale')
  }
  return translate('recommendations.status.pending')
}

function RecommendationFieldChange({
  field,
  fallbackNotSetLabel,
  translateField,
}: {
  field: ProductRecommendationField
  fallbackNotSetLabel: string
  translateField: (fieldKey: ProductRecommendationField['field']) => string
}) {
  return (
    <li className='rounded-md border border-border/70 bg-background px-2 py-1.5 text-xs'>
      <div className='font-medium text-foreground'>{translateField(field.field)}</div>
      <div className='mt-0.5 text-muted-foreground'>
        {formatFieldValue(field.currentValue, fallbackNotSetLabel)} {'→'}{' '}
        {formatFieldValue(field.proposedValue, fallbackNotSetLabel)}
      </div>
    </li>
  )
}

export function LlmChatToolProductRecommendationsCard({
  output,
  sessionId,
  productId,
  isChatStreamBusy = false,
  onContinueAfterCatalogSave,
  onContinueAfterCatalogDismiss,
}: LlmChatToolProductRecommendationsCardProps) {
  const { t: translate } = useTranslation(['llmChat', 'productCatalog'])
  const parsed = parseRecordProductContextOutput(output)
  const recommendations = parsed?.recommendations ?? []
  const persistedFacts = parsed?.persistedFacts ?? []

  const normalizedSessionId = sessionId?.trim()
  const normalizedProductId = productId?.trim()

  const recommendationsQuery = useProductRecommendations(
    normalizedProductId ?? '',
    normalizedSessionId ?? ''
  )
  const recommendationStatusById = useMemo(() => {
    const statusById = new Map<string, ProductRecommendationStatus>()
    for (const recommendation of recommendationsQuery.data ?? []) {
      statusById.set(recommendation.id, recommendation.status)
    }
    return statusById
  }, [recommendationsQuery.data])

  if (
    (recommendations.length === 0 && persistedFacts.length === 0) ||
    normalizedSessionId === undefined ||
    normalizedSessionId.length === 0 ||
    normalizedProductId === undefined ||
    normalizedProductId.length === 0
  ) {
    return null
  }

  const canResolveViaContinue =
    onContinueAfterCatalogSave !== undefined &&
    onContinueAfterCatalogDismiss !== undefined

  return (
    <div className='space-y-2'>
        {persistedFacts.length > 0 ? (
          <div className='rounded-md border border-border/70 bg-muted/20 p-3'>
            <p className='text-[12px] font-semibold text-foreground'>
              {translate('recommendations.persistedFactsTitle')}
            </p>
            <ul className='mt-2 space-y-1'>
              {persistedFacts.map((fact) => (
                <li
                  key={fact.id}
                  className='text-xs text-muted-foreground'
                >
                  <span className='font-medium text-foreground'>{fact.label}</span>
                  {': '}
                  {fact.value}
                </li>
              ))}
            </ul>
          </div>
        ) : null}
        {recommendations.map((recommendation) => {
          const storedStatus = recommendationStatusById.get(recommendation.id)
          const isPersistedRecommendation = storedStatus !== undefined
          const status = storedStatus ?? 'pending'
          const isResolved = status !== 'pending'
          const showActions =
            canResolveViaContinue && isPersistedRecommendation && !isResolved

          return (
            <article
              key={recommendation.id}
              className='rounded-md border border-border bg-background p-3'
            >
              <header className='mb-2 flex items-start justify-between gap-2'>
                <div className='min-w-0 flex-1'>
                  <p className='text-[12px] font-semibold text-foreground'>
                    {translate('recommendations.cardTitle')}
                  </p>
                  <p className='mt-0.5 text-xs text-muted-foreground'>
                    {recommendation.reason === 'empty'
                      ? translate('recommendations.reason.empty')
                      : translate('recommendations.reason.inconsistent')}
                  </p>
                </div>
                <span className='rounded bg-muted px-1.5 py-0.5 text-[11px] text-muted-foreground'>
                  {recommendationStatusLabel(status, (key) => translate(key))}
                </span>
              </header>

              <ul className='space-y-1.5'>
                {recommendation.fields.map((field) => (
                  <RecommendationFieldChange
                    key={`${recommendation.id}-${field.field}`}
                    field={field}
                    fallbackNotSetLabel={translate(
                      'productCatalog:detail.fields.notSet'
                    )}
                    translateField={(fieldKey) =>
                      translate(`productCatalog:detail.fields.${fieldKey}`)
                    }
                  />
                ))}
              </ul>

              <div className='mt-3 flex gap-2'>
                {showActions ? (
                  <>
                    <Button
                      size='xs'
                      disabled={isChatStreamBusy}
                      onClick={() => {
                        onContinueAfterCatalogSave(recommendation.id)
                      }}
                    >
                      {translate('recommendations.actions.save')}
                    </Button>
                    <Button
                      size='xs'
                      variant='outline'
                      disabled={isChatStreamBusy}
                      onClick={() => {
                        onContinueAfterCatalogDismiss(recommendation.id)
                      }}
                    >
                      {translate('recommendations.actions.ignore')}
                    </Button>
                  </>
                ) : null}
              </div>
            </article>
          )
        })}
    </div>
  )
}
